import React, { useState, useEffect } from 'react';
import apiClient from '../utils/api';
import { useAuth } from '../context/AuthContext';

// n8n webhook URL — configurable via environment variable
const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL || '';

// Roles that trigger an invitation webhook
const INVITE_ROLES = ['client', 'accountant'];

const UserManagement = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState('');
    const [confirmingDelete, setConfirmingDelete] = useState(null); // stores user id

    const [form, setForm] = useState({
        name: '',
        email: '',
        password: '',
        role: 'accountant',
        client_id: '',
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [usersRes, clientsRes] = await Promise.all([
                apiClient.get('/users'),
                apiClient.get('/clients'),
            ]);
            setUsers(usersRes.data);
            setClients(clientsRes.data);
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = 'Full Name is required.';
        if (!form.email.trim()) newErrors.email = 'Email is required.';
        if (!form.password.trim()) {
            newErrors.password = 'Password is required.';
        } else {
            // Password complexity checks to match backend
            if (form.password.length < 8) {
                newErrors.password = 'Password must be at least 8 characters.';
            } else if (!/[A-Z]/.test(form.password)) {
                newErrors.password = 'Password must contain at least one uppercase letter.';
            } else if (!/[a-z]/.test(form.password)) {
                newErrors.password = 'Password must contain at least one lowercase letter.';
            } else if (!/\d/.test(form.password)) {
                newErrors.password = 'Password must contain at least one number.';
            } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(form.password)) {
                newErrors.password = 'Password must contain at least one special character.';
            }
        }
        if (form.role === 'client' && !form.client_id) {
            newErrors.client_id = 'Please select a client.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));

        if (name === 'role' && value !== 'client') {
            setForm(prev => ({ ...prev, [name]: value, client_id: '' }));
            if (errors.client_id) setErrors(prev => ({ ...prev, client_id: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSuccess('');

        if (!validateForm()) return;

        setSubmitting(true);
        try {
            const payload = { ...form };
            if (form.role !== 'client') delete payload.client_id;

            const res = await apiClient.post('/users', payload);
            const createdUser = res.data;

            // Handle invitation status returned from backend
            if (createdUser.invite_sent) {
                setSuccess('Invitation sent successfully.');
            } else if (INVITE_ROLES.includes(form.role)) {
                // If it was supposed to be sent but invite_sent is false, it means the backend fallback or an error occurred
                setSuccess('User created, but invitation email could not be sent.');
            } else {
                setSuccess('User created successfully!');
            }

            setShowForm(false);
            setForm({ name: '', email: '', password: '', role: 'accountant', client_id: '' });
            setErrors({});
            fetchData();
        } catch (err) {
            let errorMsg = 'Failed to create user.';
            if (err.response?.data?.detail) {
                const detail = err.response.data.detail;
                if (Array.isArray(detail)) {
                    // Fastapi validation error is often an array
                    errorMsg = detail.map(d => d.msg).join(', ');
                } else {
                    errorMsg = detail;
                }
            }
            setErrors({ submit: errorMsg });
        } finally {
            setSubmitting(false);
        }
    };

    const toggleUserStatus = async (userId, isActive) => {
        try {
            const action = isActive ? 'deactivate' : 'activate';
            await apiClient.patch(`/users/${userId}/${action}`);
            fetchData();
        } catch (err) {
            alert('Failed to update user status.');
        }
    };

    const deleteUser = async (userId) => {
        try {
            await apiClient.delete(`/users/${userId}`);
            setSuccess('User deleted successfully.');
            setConfirmingDelete(null);
            fetchData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Failed to delete user.');
        }
    };

    const roleBadge = (role) => {
        const map = {
            accountant: 'bg-purple-100 text-purple-700',
            client: 'bg-teal-100 text-teal-700',
            business_owner: 'bg-blue-100 text-blue-700',
        };
        const label = {
            accountant: 'Accountant',
            client: 'Client',
            business_owner: 'Business Owner',
        };
        return (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[role] || 'bg-gray-100 text-gray-600'}`}>
                {label[role] || role}
            </span>
        );
    };

    if (loading) return <div className="p-8 text-gray-500">Loading users...</div>;

    return (
        <div className="p-8 max-w-5xl mx-auto">
            <div className="flex justify-between items-start mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
                    <p className="text-gray-500 mt-1">Create and manage Accountants and Clients</p>
                </div>
                <button
                    onClick={() => { setShowForm(!showForm); setErrors({}); setSuccess(''); }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                    {showForm ? 'Cancel' : '+ Add User'}
                </button>
            </div>

            {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
                    {success}
                </div>
            )}

            {/* Create User Form */}
            {showForm && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New User</h2>
                    {errors.submit && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                            {errors.submit}
                        </div>
                    )}
                    <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                            <input
                                name="name"
                                value={form.name}
                                onChange={handleChange}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                placeholder="John Doe"
                            />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                            <input
                                name="email"
                                type="email"
                                value={form.email}
                                onChange={handleChange}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                placeholder="user@example.com"
                            />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                            <select
                                name="role"
                                value={form.role}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="accountant">Accountant</option>
                                <option value="client">Client</option>
                            </select>
                        </div>
                        {form.role === 'client' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Link to Client *</label>
                                <select
                                    name="client_id"
                                    value={form.client_id}
                                    onChange={handleChange}
                                    className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.client_id ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                >
                                    <option value="">Select a client...</option>
                                    {clients.map(c => (
                                        <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
                                    ))}
                                </select>
                                {errors.client_id && <p className="text-red-500 text-xs mt-1">{errors.client_id}</p>}
                            </div>
                        )}
                        <div className="col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Password
                                <span className="ml-1 text-xs text-gray-400 font-normal">(min 8 chars, upper + lower + number + special)</span>
                            </label>
                            <input
                                name="password"
                                type="password"
                                value={form.password}
                                onChange={handleChange}
                                className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                placeholder="••••••••"
                            />
                            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                        </div>
                        <div className="col-span-2 flex justify-end">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
                            >
                                {submitting ? 'Creating...' : 'Create User'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                {users.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-5xl mb-4">👥</div>
                        <p className="text-gray-500">No users yet. Create your first Accountant or Client.</p>
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {users.map((u) => (
                                <tr key={u.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{u.name || '—'}</td>
                                    <td className="px-6 py-4 text-sm text-gray-600">{u.email}</td>
                                    <td className="px-6 py-4">{roleBadge(u.role)}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                            }`}>
                                            {u.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            {confirmingDelete === u.id ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-red-600 font-semibold mr-1">Sure?</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => deleteUser(u.id)}
                                                        className="text-xs font-semibold px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 cursor-pointer"
                                                    >
                                                        Yes, Delete
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmingDelete(null)}
                                                        className="text-xs font-medium px-2 py-1 text-gray-600 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleUserStatus(u.id, u.is_active)}
                                                        className={`text-xs font-medium px-3 py-1 rounded-lg w-24 text-center cursor-pointer ${u.is_active
                                                            ? 'text-red-600 border border-red-200 hover:bg-red-50'
                                                            : 'text-green-600 border border-green-200 hover:bg-green-50'
                                                            }`}
                                                    >
                                                        {u.is_active ? 'Deactivate' : 'Activate'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setConfirmingDelete(u.id)}
                                                        className="text-xs font-medium px-3 py-1 rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors cursor-pointer"
                                                    >
                                                        Delete
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default UserManagement;
